import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
	MODIFY_SPECIFIC_ASSET_UNPAIR_DEVICE_ID,
	NotFoundError,
	VelavuAPI,
	VelavuAsset,
	VelavuDevice,
} from "velavu-js-api";
import UnpairAssetTag from "../../data/unpair-asset-tag";
import { useProvideContentRefreshable } from "../../elements/refresh-context";
import { useVelavuModal } from "../../elements/velavu-modal";
import VelavuTabSwitcher from "../../elements/velavu-tab-switcher";
import { useGlobalEmitter } from "../../emitter/emitter-context";
import {
	useSubscribeCombinedAssetUpdates,
	useSubscribeCombinedDeviceUpdates,
} from "../../emitter/stream-helper";
import { useAssetImageURLCache } from "../main/asset-image-url-cache-provider";
import * as styles from "./detail-modal.module.scss";
import AssetLink from "./link/asset-link";
import TabAsset from "./tab-asset";
import TabTag from "./tab-tag";
import UnpairModal from "./unpair-modal";
import { ToastNotificationNoContent } from "../../elements/toast-notification-builtins";
import { useVelavuToast } from "../../elements/velavu-toast";

enum DetailModalTab {
	Asset,
	Device,
}

export interface DetailModalProps {
	asset?: VelavuAsset;
	device?: VelavuDevice;
	deviceOnly?: boolean;

	defaultTab?: "asset" | "device";
	defaultOpenPair?: undefined | "asset" | "tag";
	onClose?: () => void;
}

export default function DetailModal(props: DetailModalProps) {
	const { t } = useTranslation();
	const emitter = useGlobalEmitter();

	const [device, setDevice] = useState(props.device);
	const [fallbackAsset, setFallbackAsset] = useState(props.asset);
	const asset = device?.asset ?? fallbackAsset;

	const [selectedTab, setSelectedTab] = useState(
		props.defaultTab === "device"
			? DetailModalTab.Device
			: DetailModalTab.Asset,
	);

	const { pushToast } = useVelavuToast();
	const closeModal = props.onClose;
	const refreshContent = useCallback(async () => {
		//Fetch the updated object from the backend
		try {
			if (device !== undefined) {
				const updatedDevice = await VelavuAPI.devices.getSpecificDevice(
					device.id,
				);
				setDevice(updatedDevice);
			} else if (fallbackAsset !== undefined) {
				const updatedAsset = await VelavuAPI.assets.getSpecificAsset(
					fallbackAsset.id,
				);
				setFallbackAsset(updatedAsset);
			}
		} catch (error) {
			if (error instanceof NotFoundError) {
				//No more content to display; close the modal and issue a toast
				pushToast((close) => (
					<ToastNotificationNoContent onDismiss={close} />
				));
				closeModal?.();
			} else {
				throw error;
			}
		}
	}, [
		device,
		fallbackAsset,
		setDevice,
		setFallbackAsset,
		pushToast,
		closeModal,
	]);
	useProvideContentRefreshable(refreshContent);

	//Subscribe to device updates via stream
	useSubscribeCombinedDeviceUpdates(
		useCallback(
			(update) => {
				if (update.id === device?.id) {
					if (update.data === null) {
						//Try to move the asset over to the fallback asset
						setFallbackAsset(device.asset);
						setDevice(undefined);
					} else {
						setDevice(update.data);
					}
				}
			},
			[device, setDevice, setFallbackAsset],
		),
	);
	useSubscribeCombinedAssetUpdates(
		useCallback(
			(update) => {
				//Update the fallback asset
				if (update.id === fallbackAsset?.id) {
					setFallbackAsset(update.data ?? undefined);
				}

				//Update the device
				if (update.id === device?.asset?.id) {
					setDevice((device) => {
						if (device === undefined) {
							return undefined;
						} else {
							return {
								...device,
								asset: update.data ?? undefined,
							};
						}
					});
				}
			},
			[device, fallbackAsset, setFallbackAsset, setDevice],
		),
	);

	const labels = useMemo(
		() =>
			!props.deviceOnly
				? {
						[DetailModalTab.Asset]: t("asset_detail.tabs.asset"),
						[DetailModalTab.Device]: t("asset_detail.tabs.device"),
					}
				: {
						[DetailModalTab.Asset]: null,
						[DetailModalTab.Device]: t("asset_detail.tabs.device"),
					},
		[props.deviceOnly, t],
	);

	const { pushModal } = useVelavuModal();
	const unpair = useCallback(
		(data: UnpairAssetTag) => {
			//Push a conformation modal before unpairing
			pushModal((resolve) => (
				<UnpairModal
					data={data}
					onConfirm={() => resolve(true)}
					onCancel={() => resolve(false)}
				/>
			)).then((result) => {
				if (!result) return;

				//Updating the remote data
				VelavuAPI.assets
					.modifySpecificAsset(data.asset.id, {
						device_id: MODIFY_SPECIFIC_ASSET_UNPAIR_DEVICE_ID,
					})
					.then(() => {
						//Emitting the unpair event
						emitter.emit("device", {
							id: data.tag.id,
							data: {
								...data.tag,
								asset: undefined,
							},
						});

						//Updating the state
						if (data.type === "asset") {
							setFallbackAsset(undefined);
							setDevice((device) => {
								if (device === undefined) {
									return undefined;
								} else {
									return {
										...device,
										asset: undefined,
									};
								}
							});
						} else {
							setDevice(undefined);
						}
					})
					.catch((error) => console.log(error.response));
			});
		},
		[pushModal, emitter],
	);
	const pair = useCallback(
		(data: UnpairAssetTag) => {
			//Updating the remote data
			VelavuAPI.assets
				.modifySpecificAsset(data.asset.id, {
					device_id: data.tag.id,
				})
				.then(() => {
					//Updating the state
					const updatedDevice: VelavuDevice = {
						...data.tag,
						asset: data.asset,
					};
					setDevice(updatedDevice);
					emitter.emit("device", {
						id: data.tag.id,
						data: updatedDevice,
					});
				})
				.catch((error) => console.log(error.response));
		},
		[emitter],
	);

	const navigate = useNavigate();
	const navigateAssetOnMap = useMemo((): undefined | (() => void) => {
		//Check if we can navigate to this device
		if (device !== undefined) {
			//Navigate to the device on the map
			return () => {
				closeModal?.();
				navigate(`/map/device/${device.id}`);
			};
		}
	}, [asset, device, closeModal, navigate]);

	return (
		<div className={styles.container}>
			<AssetLink
				asset={asset}
				device={device}
				deviceOnly={props.deviceOnly}
				onUnpair={unpair}
				onPair={pair}
				defaultOpenPair={props.defaultOpenPair}
				onClickAssetName={navigateAssetOnMap}
			/>
			<VelavuTabSwitcher
				selectedKey={selectedTab}
				onSelectKey={setSelectedTab}
				labels={labels}
			>
				<div className={styles.content}>
					{selectedTab === DetailModalTab.Asset ? (
						asset ? (
							<TabAsset asset={asset} />
						) : (
							<TabBlank
								label={t("asset_detail.no_asset_paired")}
							/>
						)
					) : device ? (
						<TabTag tag={device} onClickMap={navigateAssetOnMap} />
					) : (
						<TabBlank label={t("asset_detail.no_tag_paired")} />
					)}
				</div>
			</VelavuTabSwitcher>
		</div>
	);
}

function TabBlank(props: { label: string }) {
	return (
		<div className={styles.tabBlank}>
			<span>{props.label}</span>
		</div>
	);
}
